import type { ThemesResult, LineId, VerifiedCitation } from '../../contract'
import { Ticker, type ToolEvent } from '../components/Ticker'
import { DroppedPanel } from '../components/DroppedPanel'
import { Quote } from '../components/Quote'
import { fmtDate } from '../format'

export interface RunState<R> {
  status: 'idle' | 'running' | 'done' | 'error'
  events: ToolEvent[]
  text: string
  result?: R
  error?: string
}

interface Props {
  run: RunState<ThemesResult>
  onRun: () => void
  onCancel: () => void
  onOpenLine: (id: LineId) => void
  knownTranscripts: Set<string>
  titles: Map<string, string>
}

export function ThemesView({ run, onRun, onCancel, onOpenLine, knownTranscripts, titles }: Props) {
  const running = run.status === 'running'
  const res = run.result
  const open = (c: VerifiedCitation) => onOpenLine(c.lineId)

  return (
    <div className="view view--themes">
      <div className="view__head">
        <div>
          <h1 className="view__title">Themes</h1>
          <p className="view__lede">Themes across every transcript. Each one is built from verbatim quotes, at least two, from at least two interviews.</p>
        </div>
        <div className="view__actions">
          {running ? (
            <button className="btn" onClick={onCancel} data-testid="cancel-themes">Stop</button>
          ) : (
            <button className="btn btn--primary" onClick={onRun} data-testid="run-themes">{res ? 'Run again' : 'Find themes'}</button>
          )}
        </div>
      </div>

      {(running || run.events.length > 0) && (
        <Ticker events={run.events} status={running ? run.text || 'Thinking…' : `Done · ${res?.model ?? ''}`} running={running} />
      )}
      {run.status === 'error' && <p className="error" role="alert">Run failed: {run.error}</p>}

      {run.status === 'idle' && (
        <div className="hero card" data-testid="themes-empty">
          <h2>Nothing ships without a receipt.</h2>
          <p>Press <strong>Find themes</strong>. The model searches the corpus with tools, proposes themes, and every quote it offers is checked against the transcript text, verbatim. A theme keeps only quotes that check out, and needs two of them from two different interviews.</p>
          <p>Claims that fail land in the <strong>Dropped</strong> panel with the reason, so you can see what the model wanted to say and why it wasn't allowed to.</p>
        </div>
      )}

      {res && (
        <div className="themes-layout">
          <div className="themes-main">
            <div className="resultbar">
              <span>
                <strong data-testid="theme-count">{res.themes.length}</strong> themes across {res.transcriptIds.length} transcripts
                {res.dropped.length > 0 && <> · <button className="resultbar__drop" onClick={() => document.querySelector('[data-testid=dropped-panel]')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>{res.dropped.length} dropped ↓</button></>}
              </span>
              <span className="muted">model <span className={`chip${res.model === 'stub' ? ' chip--warn' : ''}`} data-testid="model-chip">{res.model}</span> · {fmtDate(res.generatedAt)}</span>
            </div>
            {res.themes.length === 0 && <p className="empty">No theme survived verification. Everything is in the Dropped panel.</p>}
            <div className="theme-grid">
              {res.themes.map((th) => (
                <article className="theme card" key={th.id} data-testid="theme-card">
                  <header className="theme__head">
                    <h2 className="theme__title">{th.title}</h2>
                    <span className="theme__src" title={th.transcriptIds.join(', ')}>{th.citations.length} quotes · {th.transcriptIds.length} interviews</span>
                  </header>
                  <p className="theme__summary">{th.summary}</p>
                  <div className="theme__quotes">
                    {th.citations.map((c, i) => <Quote key={i} cite={c} onOpen={open} titles={titles} />)}
                  </div>
                </article>
              ))}
            </div>
          </div>
          <DroppedPanel dropped={res.dropped} ran onOpenLine={onOpenLine} knownTranscripts={knownTranscripts} />
        </div>
      )}
    </div>
  )
}
